import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, MapPin } from '@phosphor-icons/react';

interface Props {
  className?: string;
  variant?: 'light' | 'dark';
  centered?: boolean;
}

interface Suggestion {
  id: number;
  slug: string | null;
  title: string;
  category_name: string | null;
  category_slug: string | null;
  region_name: string | null;
}

const API_BASE = import.meta.env.VITE_API_URL || '/.netlify/functions';

export default function SearchBar({ className = '', variant = 'light', centered = true }: Props) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const rootRef = useRef<HTMLFormElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const isDark = variant === 'dark';

  // Debounced suggest fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      setActive(-1);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/listings?suggest=1&search=${encodeURIComponent(q)}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('suggest failed');
        const data: Suggestion[] = await res.json();
        setSuggestions(data);
        setOpen(true);
        setActive(-1);
      } catch {
        // aborted or failed — keep current state
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const goToListing = useCallback(
    (s: Suggestion) => {
      setOpen(false);
      setQuery(s.title);
      if (s.category_slug && s.slug) {
        navigate(`/listing/${s.category_slug}/${s.slug}`);
      } else {
        navigate(`/listing/${s.id}`);
      }
    },
    [navigate]
  );

  const submitSearch = useCallback(() => {
    setOpen(false);
    navigate(`/browse?search=${encodeURIComponent(query.trim())}`);
  }, [navigate, query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (active >= 0 && suggestions[active]) {
      goToListing(suggestions[active]);
    } else {
      submitSearch();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <form
      ref={rootRef}
      onSubmit={handleSubmit}
      className={`relative w-full max-w-[600px] ${centered ? 'mx-auto' : ''} flex flex-col sm:flex-row gap-2 p-2 rounded-lg shadow-lg backdrop-blur-md ${className} ${
        isDark
          ? 'bg-white/10 border border-white/20'
          : 'bg-surface-light dark:bg-surface border border-[rgba(79,179,217,0.2)] dark:border-[rgba(79,179,217,0.25)]'
      }`}
    >
      <div className="relative flex-1 min-w-0">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder="Search charters, chandlers, services..."
          aria-label="Search"
          aria-expanded={open}
          aria-autocomplete="list"
          role="combobox"
          autoComplete="off"
          className={`w-full px-4 sm:px-5 py-3 sm:py-4 bg-transparent border-none text-sm sm:text-base outline-none ${
            isDark
              ? 'text-white placeholder:text-white/60'
              : 'text-text-primary-light dark:text-text-primary placeholder:text-text-muted'
          }`}
        />

        {/* Suggestions dropdown */}
        {open && suggestions.length > 0 && (
          <div
            role="listbox"
            className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 rounded-xl border border-[rgba(16,42,66,0.12)] dark:border-[rgba(214,233,247,0.14)] bg-white dark:bg-[#0b2233] shadow-xl overflow-hidden text-left"
          >
            {suggestions.map((s, i) => (
              <button
                key={s.id}
                type="button"
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => goToListing(s)}
                className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                  i === active
                    ? 'bg-[rgba(43,169,220,0.12)]'
                    : 'hover:bg-[rgba(43,169,220,0.06)]'
                } ${i > 0 ? 'border-t border-[rgba(16,42,66,0.07)] dark:border-[rgba(214,233,247,0.08)]' : ''}`}
              >
                <MagnifyingGlass size={15} weight="bold" className="flex-shrink-0 text-[#2ba9dc]" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-text-primary-light dark:text-white truncate">
                    {s.title}
                  </span>
                  <span className="block text-xs text-text-muted truncate mt-0.5">
                    {s.category_name}
                    {s.region_name ? ` · ${s.region_name}` : ''}
                  </span>
                </span>
                {s.region_name && (
                  <MapPin size={14} className="flex-shrink-0 text-text-muted" />
                )}
              </button>
            ))}
            <button
              type="submit"
              className="w-full px-4 py-2.5 text-xs font-semibold text-[#2ba9dc] bg-[rgba(43,169,220,0.07)] hover:bg-[rgba(43,169,220,0.14)] transition-colors border-t border-[rgba(16,42,66,0.07)] dark:border-[rgba(214,233,247,0.08)]"
            >
              See all results for "{query.trim()}"
            </button>
          </div>
        )}

        {/* Loading shimmer (subtle, right side of input) */}
        {loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-[#2ba9dc]/30 border-t-[#2ba9dc] animate-spin" />
        )}
      </div>

      <button type="submit" className="btn btn-primary px-6 py-3 text-sm whitespace-nowrap flex items-center justify-center gap-2">
        <MagnifyingGlass size={17} weight="bold" className="flex-shrink-0" />
        <span>Search</span>
      </button>
    </form>
  );
}
